import { useState, useEffect, useMemo } from 'react';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaHashtag, FaVolumeUp, FaPlus, FaBell, FaStar, FaRegStar, FaLock } from 'react-icons/fa';
import api from '@/services/api';
import socketService from '@/services/socket';
import { apiCache } from '@/utils/apiCache';
import { favoritesCache } from '@/services/favoritesCache';

import type { Channel } from '@/types';
import UserSettingsModal from '@/components/settings/UserSettingsModal';
import ChannelNotificationSettings from '@/components/settings/ChannelNotificationSettings';

interface ChannelSidebarProps {
  currentChannelId: string | null;
  onChannelSelect: (channelId: string) => void;
  onCreateChannel?: () => void;
  favoriteChannels?: Set<string>;
  onToggleFavorite?: (channelId: string) => Promise<void>;
  onDashboardClick?: () => void;
  onAdminClick?: () => void;
  currentView?: 'channel' | 'dashboard' | 'admin';
}

export default function ChannelSidebar({
  currentChannelId,
  onChannelSelect,
  onCreateChannel,
  favoriteChannels: parentFavorites,
  onToggleFavorite: parentToggleFavorite,
  onDashboardClick,
  onAdminClick,
  currentView,
}: ChannelSidebarProps) {
  const currentUser = useCurrentUser();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  const [selectedChannelForNotifications, setSelectedChannelForNotifications] = useState<{
    id: string;
    name: string;
    isVoice: boolean;
  } | null>(null);
  const [avatarSrc, setAvatarSrc] = useState<string>('');
  const [voiceActivity, setVoiceActivity] = useState<
    Record<string, { users: { id: string; username: string }[] }>
  >({});
  const [inVoiceCall, setInVoiceCall] = useState<boolean>(
    sessionStorage.getItem('inVoiceCall') === 'true',
  );
  // Use parent favorites if provided, otherwise manage locally
  const [localFavorites, setLocalFavorites] = useState<Set<string>>(new Set());
  const favoriteChannels = parentFavorites || localFavorites;
  const navigate = useNavigate();
  const location = useLocation();

  const goToDashboard = () => {
    if (onDashboardClick) {
      onDashboardClick();
    } else {
      if (currentChannelId) {
        onChannelSelect('');
      }
      navigate('/chat?dashboard=1', { replace: false });
    }
  };

  const goToAdmin = () => {
    if (onAdminClick) {
      onAdminClick();
    } else {
      navigate('/admin');
    }
  };

  useEffect(() => {
    // Only fetch if user is authenticated
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoading(false);
      return;
    }

    fetchChannels();
    fetchVoiceActivity();
    // Only fetch if not provided by parent
    if (!parentFavorites) {
      fetchFavoriteChannels();
    }
  }, [parentFavorites]);

  useEffect(() => {
    const handleChannelsChanged = () => {
      // Invalidate cache
      apiCache.clear(/^channels:/);
      fetchChannels();
      fetchVoiceActivity();
    };
    const handleVoiceJoin = (data: any) => {
      setVoiceActivity((prev) => {
        const users = prev[data.channelId]?.users || [];
        if (users.some((u) => String(u.id) === String(data.userId))) return prev;
        return {
          ...prev,
          [data.channelId]: {
            users: [...users, { id: data.userId, username: data.username }],
          },
        };
      });
    };

    const handleVoiceLeave = (data: any) => {
      setVoiceActivity((prev) => {
        const users = prev[data.channelId]?.users || [];
        const filtered = users.filter((u) => String(u.id) !== String(data.userId));
        if (users.length === filtered.length) return prev;
        return {
          ...prev,
          [data.channelId]: { users: filtered },
        };
      });
    };

    socketService.on('admin:channel-created', handleChannelsChanged);
    socketService.on('admin:channels-reordered', handleChannelsChanged);
    socketService.on('admin:channel-deleted', handleChannelsChanged);
    socketService.on('admin:channel-updated', handleChannelsChanged);
    socketService.on('admin:channel-member-added', handleChannelsChanged);
    socketService.on('admin:channel-member-updated', handleChannelsChanged);
    socketService.on('admin:channel-member-removed', handleChannelsChanged);
    socketService.on('voice:user-joined', handleVoiceJoin);
    socketService.on('voice:user-left', handleVoiceLeave);

    const handleStorage = () => {
      setInVoiceCall(sessionStorage.getItem('inVoiceCall') === 'true');
    };
    window.addEventListener('storage', handleStorage);

    // Refresh cache on reconnection to ensure data is current
    const unsubscribe = socketService.onConnectionChange((connected, reconnecting) => {
      if (connected && !reconnecting) {
        // Clear cache and refetch after successful reconnection
        apiCache.clear(/^channels:/);
        apiCache.clear(/^voice:/);
        fetchChannels();
        fetchVoiceActivity();
      }
    });

    return () => {
      socketService.off('admin:channel-created', handleChannelsChanged);
      socketService.off('admin:channels-reordered', handleChannelsChanged);
      socketService.off('admin:channel-deleted', handleChannelsChanged);
      socketService.off('admin:channel-updated', handleChannelsChanged);
      socketService.off('admin:channel-member-added', handleChannelsChanged);
      socketService.off('admin:channel-member-updated', handleChannelsChanged);
      socketService.off('admin:channel-member-removed', handleChannelsChanged);
      socketService.off('voice:user-joined', handleVoiceJoin);
      socketService.off('voice:user-left', handleVoiceLeave);
      window.removeEventListener('storage', handleStorage);
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let refreshTimer: NodeJS.Timeout;

    const refreshAvatar = () => {
      const raw = currentUser.avatarUrl as string | undefined;
      if (raw) {
        const filename = raw.split('/').pop();
        api
          .get('/uploads/sign', { params: { filename } })
          .then((res) => {
            setAvatarSrc(res.data.url);
            // Refresh 30s before expiry
            const expiresIn = (res.data.exp - Math.floor(Date.now() / 1000)) * 1000;
            if (expiresIn > 30000) {
              refreshTimer = setTimeout(refreshAvatar, expiresIn - 30000);
            }
          })
          .catch(() => setAvatarSrc(raw));
      } else {
        setAvatarSrc('');
      }
    };
    refreshAvatar();
    return () => clearTimeout(refreshTimer);
  }, [settingsOpen]);

  const fetchChannels = async () => {
    try {
      // Check cache first
      const cacheKey = 'channels:list';
      const cached = apiCache.get<{ channels: Channel[] }>(cacheKey);
      if (cached) {
        setChannels(cached.channels);
        setLoading(false);
        return;
      }

      const response = await api.get('/channels');
      // Cache for 15 minutes - WebSocket handles real-time updates
      apiCache.set(cacheKey, response.data, 15 * 60 * 1000);
      setChannels(response.data.channels);
    } catch (error: any) {
      // Silently ignore 401 errors when not authenticated
      if (error.response?.status !== 401) {
        console.error('Error fetching channels:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVoiceActivity = async () => {
    try {
      // Check cache first
      const cacheKey = 'voice:sessions';
      const cached = apiCache.get<any>(cacheKey);
      if (cached) {
        const nextState: Record<string, { users: { id: string; username: string }[] }> = {};
        (cached.sessions || []).forEach((entry: any) => {
          nextState[entry.channel.id] = {
            users: (entry.users || []).map((u: any) => ({ id: u.id, username: u.username })),
          };
        });
        setVoiceActivity(nextState);
        return;
      }

      const response = await api.get('/voice/sessions');
      // Cache for 15 minutes - WebSocket handles real-time join/leave events instantly
      apiCache.set(cacheKey, response.data, 15 * 60 * 1000);

      const nextState: Record<string, { users: { id: string; username: string }[] }> = {};
      (response.data.sessions || []).forEach((entry: any) => {
        nextState[entry.channel.id] = {
          users: (entry.users || []).map((u: any) => ({ id: u.id, username: u.username })),
        };
      });
      setVoiceActivity(nextState);
    } catch (error: any) {
      // Silently ignore 401 errors when not authenticated
      if (error.response?.status !== 401) {
        console.error('Error fetching voice session activity:', error);
      }
    }
  };

  const toggleFavorite = async (channelId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    console.debug('[ChannelSidebar] Star button clicked for channel:', channelId);
    try {
      if (parentToggleFavorite) {
        // Use parent toggle function
        console.debug('[ChannelSidebar] Using parent toggle function');
        await parentToggleFavorite(channelId);
      } else {
        console.debug('[ChannelSidebar] Using local toggle');
        // Use local toggle
        if (favoriteChannels.has(channelId)) {
          await api.delete(`/favorite-channels/${channelId}`);
          setLocalFavorites((prev) => {
            const updated = new Set(prev);
            updated.delete(channelId);
            return updated;
          });
        } else {
          await api.post(`/favorite-channels/${channelId}`);
          setLocalFavorites((prev) => new Set([...prev, channelId]));
        }
        // Clear cache to force refetch
        favoritesCache.clear();
        // Refresh favorites from server to ensure consistency
        await fetchFavoriteChannels();
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
    }
  };

  const fetchFavoriteChannels = async () => {
    try {
      const response = await api.get('/favorite-channels');
      const favoriteIds = new Set<string>(
        (response.data.favoriteChannels || []).map((fav: any) => fav.channelId),
      );
      setLocalFavorites(favoriteIds);
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Error fetching favorite channels:', error);
      }
    }
  };

  // Memoize filtered channels to avoid O(N) reallocation on every render when presence/channels change
  // Combined into a single O(N) pass for better performance
  const { textChannels, voiceChannels } = useMemo(() => {
    const text: Channel[] = [];
    const voice: Channel[] = [];
    for (let i = 0; i < channels.length; i++) {
      if (channels[i].isVoice) voice.push(channels[i]);
      else text.push(channels[i]);
    }
    return { textChannels: text, voiceChannels: voice };
  }, [channels]);

  // Focus currently selected channel when it changes
  useEffect(() => {
    if (currentChannelId) {
      const el = document.querySelector(
        `[data-channel-id="${currentChannelId}"]`,
      ) as HTMLElement | null;
      el?.focus();
    }
  }, [currentChannelId]);

  const handleListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const items = Array.from(container.querySelectorAll<HTMLElement>('.channel-item'));
    if (items.length === 0) return;
    const active = document.activeElement as HTMLElement;
    const idx = items.findIndex((el) => el === active);
    if (idx === -1) {
      // Focus current selection if none focused
      const current =
        container.querySelector<HTMLElement>(`[data-channel-id="${currentChannelId}"]`) || items[0];
      current?.focus();
      return;
    }

    const key = e.key;
    if (key === 'ArrowDown' || key === 'ArrowRight') {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (key === 'ArrowUp' || key === 'ArrowLeft') {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
      e.preventDefault();
      const focused = items[idx];
      const chanId = focused?.getAttribute('data-channel-id');
      if (chanId) onChannelSelect(chanId);
    }
  };

  if (loading) {
    return (
      <div className="w-64 glass-effect text-brand-on-primary p-4">
        <div className="animate-pulse">Loading channels...</div>
      </div>
    );
  }

  const isAdminOrOwner = !!(currentUser?.isAdmin || (currentUser as any)?.isOwner);

  return (
    <div className="w-64 glass-effect text-brand-on-primary flex flex-col h-full">
      <div className="pt-6 pb-3 px-4 border-b border-brand-outline flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={goToDashboard}
          className="focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none rounded-2xl p-1 cursor-pointer transition-transform hover:scale-[1.03] active:scale-[0.98]"
          title="Open user dashboard"
          aria-label="Open User Dashboard"
        >
          <img
            src="/irus-logo.svg"
            alt="Irus Logo"
            className="w-40 h-40 drop-shadow-md"
          />
        </button>
        <button
          onClick={goToDashboard}
          className={`w-full py-2 px-4 text-xs sm:text-sm font-semibold rounded-full border transition-all text-center cursor-pointer active:scale-[0.98] ${
            (currentView === 'dashboard' || (!currentView && location.pathname !== '/admin' && currentChannelId === null))
              ? 'bg-primary-600/35 border-primary-500 text-white shadow-md shadow-primary-500/20 font-bold'
              : 'bg-brand-surface/70 dark:bg-white/5 text-brand-on-surface-variant dark:text-[#e0e7ff] border-brand-outline-variant/60 hover:bg-white/10 hover:border-primary-400 hover:text-white'
          }`}
          aria-current={(currentView === 'dashboard' || (!currentView && location.pathname !== '/admin' && currentChannelId === null)) ? 'page' : undefined}
        >
          📊 Dashboard
        </button>
        {isAdminOrOwner && (
          <button
            onClick={goToAdmin}
            className={`w-full py-2 px-4 text-xs sm:text-sm font-semibold rounded-full border transition-all text-center cursor-pointer active:scale-[0.98] ${
              (currentView === 'admin' || (!currentView && location.pathname === '/admin'))
                ? 'bg-primary-600/35 border-primary-500 text-white shadow-md shadow-primary-500/20 font-bold'
                : 'bg-brand-surface/70 dark:bg-white/5 text-brand-on-surface-variant dark:text-[#e0e7ff] border-brand-outline-variant/60 hover:bg-white/10 hover:border-primary-400 hover:text-white'
            }`}
            aria-current={(currentView === 'admin' || (!currentView && location.pathname === '/admin')) ? 'page' : undefined}
          >
            🛡️ Admin Panel
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div
          className="p-2"
          role="listbox"
          aria-label="Text Channels"
          tabIndex={0}
          onFocus={() => {
            const current = document.querySelector<HTMLElement>(
              `[data-channel-id="${currentChannelId}"]`,
            );
            current?.focus();
          }}
          onKeyDown={handleListKeyDown}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base uppercase text-brand-on-surface-variant font-bold">
              Text Channels
            </h3>
            {onCreateChannel && (
              <button
                onClick={onCreateChannel}
                className="p-1 rounded text-brand-on-surface-variant hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Create Channel"
                title="Create Channel"
              >
                <FaPlus size={12} />
              </button>
            )}
          </div>
          {textChannels.map((channel, i) => (
            <div
              key={channel.id}
              role="option"
              tabIndex={currentChannelId === channel.id ? 0 : i === 0 ? 0 : -1}
              data-channel-id={channel.id}
              aria-selected={currentChannelId === channel.id}
              onKeyDown={(e) => {
                // Prevent nested buttons from hijacking Enter/Space
                if (
                  (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') &&
                  (e.target as HTMLElement).classList.contains('channel-item')
                ) {
                  e.preventDefault();
                  onChannelSelect(channel.id);
                }
              }}
              className={`w-full text-left px-3 py-2.5 min-h-[44px] rounded-lg mb-1.5 flex items-center gap-2.5 group cursor-pointer transition-all channel-item touch-manipulation active:scale-[0.98] ${
                currentChannelId === channel.id
                  ? 'bg-primary-600/30 border-l-4 border-primary-500 text-white font-semibold shadow-md shadow-primary-600/10 dark:bg-primary-500/25'
                  : 'text-brand-on-surface-variant hover:bg-brand-surface/90 dark:hover:bg-white/10 hover:text-white hover:translate-x-0.5'
              }`}
              onClick={() => onChannelSelect(channel.id)}
            >
              <FaHashtag size={14} className={currentChannelId === channel.id ? 'text-primary-400' : 'text-brand-outline'} />
              <span className="text-sm flex-1 truncate">{channel.name}</span>
              <button
                onClick={(e) => {
                  console.debug('[ChannelSidebar] onClick fired on button');
                  toggleFavorite(channel.id, e);
                }}
                className="p-1.5 rounded-md opacity-100 hover:bg-white/15 active:scale-95 transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none"
                aria-label={
                  favoriteChannels.has(channel.id) ? 'Remove from favorites' : 'Add to favorites'
                }
                title={
                  favoriteChannels.has(channel.id) ? 'Remove from favorites' : 'Add to favorites'
                }
              >
                {favoriteChannels.has(channel.id) ? (
                  <FaStar size={12} className="text-yellow-400" />
                ) : (
                  <FaRegStar size={12} className="text-brand-outline hover:text-yellow-300" />
                )}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedChannelForNotifications({
                    id: channel.id,
                    name: channel.name,
                    isVoice: channel.isVoice,
                  });
                  setNotificationSettingsOpen(true);
                }}
                className="p-1.5 rounded-md opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 hover:bg-white/15 active:scale-95 transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none touch-manipulation"
                aria-label={`Notification settings for ${channel.name}`}
                title="Notification settings"
              >
                <FaBell size={12} className="text-brand-outline hover:text-white" />
              </button>
            </div>
          ))}
        </div>

        {voiceChannels.length > 0 && (
          <div className="p-2">
            <h3 className="text-base uppercase text-brand-on-surface-variant font-bold mb-2">
              Voice Channels
            </h3>
            {voiceChannels.map((channel, i) => (
              <div
                key={channel.id}
                className={`w-full text-left px-3 py-2.5 min-h-[44px] rounded-lg mb-1.5 flex items-center gap-2.5 group cursor-pointer transition-all channel-item touch-manipulation active:scale-[0.98] ${
                  currentChannelId === channel.id
                    ? 'bg-primary-600/30 border-l-4 border-primary-500 text-white font-semibold shadow-md shadow-primary-600/10 dark:bg-primary-500/25'
                    : 'text-brand-on-surface-variant hover:bg-brand-surface/90 dark:hover:bg-white/10 hover:text-white hover:translate-x-0.5'
                }`}
                role="option"
                tabIndex={currentChannelId === channel.id ? 0 : i === 0 ? 0 : -1}
                data-channel-id={channel.id}
                aria-selected={currentChannelId === channel.id}
                onClick={() => onChannelSelect(channel.id)}
              >
                <FaVolumeUp size={14} className={currentChannelId === channel.id ? 'text-primary-400' : 'text-brand-outline'} />
                <span className="text-sm flex-1 flex items-center gap-1.5">
                  {channel.name}
                  <FaLock size={9} className="text-emerald-400 opacity-80 flex-shrink-0" title="End-to-End Encrypted Voice (DTLS-SRTP)" />
                </span>
                {inVoiceCall && (voiceActivity[channel.id]?.users?.length || 0) > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-brand-on-primary">
                    {voiceActivity[channel.id].users.length}
                  </span>
                )}
                <button
                  onClick={(e) => {
                    console.debug('[ChannelSidebar] onClick fired on button');
                    toggleFavorite(channel.id, e);
                  }}
                  className="p-1 rounded opacity-100 hover:bg-brand-surface/50 dark:bg-brand-background/30 transition-opacity focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none"
                  aria-label={
                    favoriteChannels.has(channel.id) ? 'Remove from favorites' : 'Add to favorites'
                  }
                  title={
                    favoriteChannels.has(channel.id) ? 'Remove from favorites' : 'Add to favorites'
                  }
                >
                  {favoriteChannels.has(channel.id) ? (
                    <FaStar size={12} className="text-yellow-500" />
                  ) : (
                    <FaRegStar size={12} />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedChannelForNotifications({
                      id: channel.id,
                      name: channel.name,
                      isVoice: channel.isVoice,
                    });
                    setNotificationSettingsOpen(true);
                  }}
                  className="p-1 rounded opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 hover:bg-brand-surface/50 dark:bg-brand-background/30 transition-opacity focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none touch-manipulation"
                  aria-label={`Notification settings for ${channel.name}`}
                  title="Notification settings"
                >
                  <FaBell size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-brand-outline-variant/60 space-y-2 bg-brand-surface/40 dark:bg-black/20">
        <button
          className="w-full flex items-center gap-3 text-left hover:bg-white/10 rounded-2xl px-3 py-2.5 min-h-[48px] focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none transition-all active:scale-[0.98] cursor-pointer"
          onClick={() => setSettingsOpen(true)}
          aria-label="Open user settings"
        >
          {(() => {
            const initial = currentUser.username?.[0]?.toUpperCase() || 'U';
            const status = currentUser.status || 'online';
            const avatarUrl = avatarSrc;
            const statusBg =
              status === 'online'
                ? 'bg-green-500'
                : status === 'idle' || status === 'away'
                  ? 'bg-yellow-500'
                  : status === 'dnd'
                    ? 'bg-red-500'
                    : 'bg-gray-400';
            const statusLabel =
              status === 'online'
                ? 'Online'
                : status === 'idle' || status === 'away'
                  ? 'Idle'
                  : status === 'dnd'
                    ? 'Do Not Disturb'
                    : 'Offline';
            return (
              <>
                <div className="relative flex-shrink-0">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="avatar" className="w-9 h-9 rounded-full object-cover shadow-sm" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-primary-600 flex items-center justify-center shadow-sm">
                      <span className="text-sm font-bold text-white">{initial}</span>
                    </div>
                  )}
                  <div
                    className={`absolute bottom-0 right-0 w-3 h-3 ${statusBg} rounded-full border-2 border-brand-surface dark:border-[#1a1740] shadow-sm`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff] truncate">{currentUser.username || 'User'}</div>
                  <div className="text-xs text-brand-outline dark:text-[#c4b5fd] truncate">{statusLabel}</div>
                </div>
              </>
            );
          })()}
        </button>
        <UserSettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
        {selectedChannelForNotifications && (
          <ChannelNotificationSettings
            isOpen={notificationSettingsOpen}
            onClose={() => {
              setNotificationSettingsOpen(false);
              setSelectedChannelForNotifications(null);
            }}
            channelId={selectedChannelForNotifications.id}
            channelName={selectedChannelForNotifications.name}
            isVoice={selectedChannelForNotifications.isVoice}
          />
        )}
      </div>
    </div>
  );
}
